import { useState, type FC } from 'react';
import type { NavbarProps } from 'shared/types/layout.type';
import { useTranslation } from 'react-i18next';
import { NavbarContainer } from './navbar.style';
import Menu from 'utils/navbar.config';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import NavbarItem from './navbar-item';
import { MenuOutline, UserOutline, LoginActivityOutline } from '@coinlocally/cly-core/icons';
import HamburgerMenu from './hamburger-menu';
import { Button, ClyCoreConfig } from '@coinlocally/cly-core';
import { useAppSelector, useBreakpoint } from 'shared/hooks';
import Profile from './profile';
import { LocalSelector } from 'shared/components';
import { ChangeLanguageModal } from 'shared/components/modal';

const Navbar: FC<NavbarProps> = ({ navbarType = 'FULL', showTopNavBar = true }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const [hamburgerStatus, setHamburgerStatus] = useState<'profile' | 'general' | ''>('');
	const [lngModalStatus, setLngModalStatus] = useState<boolean>(false);
	const isLogin = useAppSelector(store => store.Profile.isLogin);
	const { t, i18n } = useTranslation('layout', { keyPrefix: 'navbar' });
	const menu = Menu();
	const isTablet = useBreakpoint('max', 'xl');

	const onChangeLanguageHandler = (lng: string) => {
		if (lng !== i18n.language) {
			ClyCoreConfig.LANGUAGE = lng as 'en' | 'ru' | 'fa' | 'ar' | 'de' | 'es';
			navigate(location.pathname.replace(i18n.language, lng), { replace: true });
			i18n.changeLanguage(lng);
		}
	};

	return (
		<NavbarContainer>
			{showTopNavBar ? (
				<div className='top_container'>
					{isTablet ? (
						isLogin ? (
							<Button className='left_button' variant='outline' size='small' href={`/${i18n.language}/dashboard/accounts`}>
								{t('try-demo')}
							</Button>
						) : (
							<Button className='left_button' variant='outline' size='small' href={`/${i18n.language}/login`}>
								{t('login')}
							</Button>
						)
					) : (
						<>
							<Link to={`/${i18n.language}/dashboard/accounts`} className='demo_link'>
								{t('try-demo')}
							</Link>

							{isLogin ? null : (
								<Button className='left_button' variant='outline' size='small' href={`/${i18n.language}/login`}>
									{t('login')}
								</Button>
							)}
						</>
					)}

					<Button className='right_button' variant='secondary' size='small' href={`/${i18n.language}/dashboard/accounts`}>
						{t('open-live')}
					</Button>
				</div>
			) : null}

			<div className='bottom_container'>
				<div className='left_field'>
					<Link to={`/${i18n.language}`}>
						<img src='/img/logo.svg' alt='logo' />
					</Link>

					{navbarType === 'FULL' ? (
						<div className='menu_list'>
							{menu.map((item: any, index: any) => (
								<NavbarItem
									title={item.title}
									subMenu={item.subMenu ?? null}
									link={item.link}
									href={item.href}
									key={`navbar_item_${index}`}
									isNew={item.isNew ?? false}
								/>
							))}
						</div>
					) : null}
				</div>

				<div className='right_field'>
					{navbarType === 'FULL' ? (
						<>
							{isLogin ? (
								<>
									{isTablet ? (
										<button className='icon_button' onClick={() => setHamburgerStatus('profile')}>
											<UserOutline />
										</button>
									) : (
										<Profile />
									)}
								</>
							) : !showTopNavBar ? (
								<Link to={`/${i18n.language}/login`} className='login_link'>
									<LoginActivityOutline />
								</Link>
							) : null}
							<div className='divider' />
						</>
					) : null}

					{!isTablet && <LocalSelector languageList={['en', 'fa', 'de', 'fr', 'ru', 'es']} />}

					{isTablet && (
						<button className='icon_button' onClick={() => setHamburgerStatus('general')}>
							<MenuOutline />
						</button>
					)}
				</div>
			</div>

			{lngModalStatus ? (
				<ChangeLanguageModal status={lngModalStatus} setStatus={setLngModalStatus} onLanguageClick={onChangeLanguageHandler} />
			) : null}

			{hamburgerStatus !== '' ? (
				<HamburgerMenu
					status={['general', 'profile'].includes(hamburgerStatus)}
					onClose={() => setHamburgerStatus('')}
					type={hamburgerStatus}
				/>
			) : null}
		</NavbarContainer>
	);
};

export default Navbar;
